import type { ModelRuntime } from "@/api/types";

/**
 * provider 名 → 人话。
 *
 * 取值必须覆盖后端可能给出的一切（`FakeProvider.name="fake"` · `HttpProvider.name="http"`
 * · `ResponsesProvider.name="responses"`）。未识别值**原样呈现**——把未知的 provider
 * 说成"真实模型"，比显示一个陌生字符串危险得多。
 */
const PROVIDER_LABEL: Record<string, string> = {
  fake: "演示（未调用模型）",
  http: "HTTP",
  responses: "Responses",
};

export function providerLabel(provider: string | null): string {
  if (!provider) return "—";
  return PROVIDER_LABEL[provider] ?? provider;
}

/** 这次产出是否由真实模型生成。`null`（该列存在之前的运行）**不猜测**。 */
export function isRealProvider(provider: string | null): boolean {
  return provider === "http" || provider === "responses";
}

/**
 * 这次产出是否来自确定性假模型。
 *
 * 单列一个函数而不是内联 `provider === "fake"`：`fake` 是**后端**的约定值
 * （`FakeProvider.name`），散落在各个视图里的话，后端一改名就会静默失效——
 * 而失效的形态恰好是"警告不再显示"，最难被发现。
 */
export function isDemoProvider(provider: string | null): boolean {
  return provider === "fake";
}

/**
 * `mode` / `source` → 一句话说明。返回空串表示"无需额外说明"。
 *
 * 枚举值来自后端（`ModelRuntime`），前端只做呈现——**不得**从 `enabled` 自己推：
 * `enabled=false` 时"存着配置"与"不会调用模型"同时为真，只有运行解析结果能分辨。
 */
export function runtimeNote(runtime: ModelRuntime): string {
  if (runtime.mode === "demo") {
    return "当前不会调用真实模型，产出由确定性假模型生成，仅供试跑。";
  }
  if (runtime.mode === "real") return "";
  switch (runtime.source) {
    case "disabled":
      return "真实模型已停用，需先在设置页启用后再生成。";
    case "invalid":
      return "保存的模型配置无效，需先在设置页重新保存后再生成。";
    default:
      return "需先在设置页配置模型后再生成。";
  }
}

/**
 * 生成入口是否应当置灰。
 *
 * **只有** `unavailable` 会阻断：`demo` 必须放行，否则测试环境与本地开发一步都走不通，
 * 那会把人推回"配一个真实 Key 才能开发"。
 */
export function runtimeBlocksGeneration(runtime: ModelRuntime): boolean {
  return runtime.mode === "unavailable";
}

/**
 * 设置页用：无论哪种去向都要说清"接下来按什么跑"。
 *
 * 参数刻意允许 `undefined`，尽管类型上 `ModelSettings.runtime` 是必填的：
 * 那是**当前后端**的真实形状，而这里是**契约边界**——响应里一旦没有这个块
 * （旧后端、网关截断、测试替身形态），`runtime.mode` 会直接抛错，
 * 结果是设置页整页崩掉，而这恰恰是最需要看到说明的页面。
 * 说一句"无法确认"是显式状态，不是静默回落。
 */
export function runtimeSummary(runtime: ModelRuntime | undefined): string {
  if (!runtime) return "暂时无法确认当前会使用哪个模型，请重新加载配置。";
  if (runtime.mode === "real") {
    const where = runtime.source === "saved" ? "保存的配置" : "环境变量";
    const model = runtime.model ? ` · ${runtime.model}` : "";
    return `当前按${where}运行：${providerLabel(runtime.provider)}${model}。`;
  }
  return runtimeNote(runtime) || "当前没有可用的模型。";
}
